import React, { useState, useEffect } from "react";

//MUI components
import {
    Dialog as MuiDialog,
    DialogTitle as MuiDialogTitle,
    DialogContent as MuiDialogContent,
    Grid,
    TextField,
    MenuItem,
    Typography,
    IconButton,
    Tooltip,
    InputAdornment,
    FormControlLabel,
    Switch,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";

//Icons
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";

//Custom portal components
import FooterControls from "../FooterComponents/FooterControls";

//External libraries
import Swal from "sweetalert2";

const Dialog = withStyles((theme) => ({
    root: {
        marginTop: "56px",
        [theme.breakpoints.up("md")]: {
            marginTop: "64px",
        },
    },
}))(MuiDialog);

const DialogTitle = withStyles(() => ({
    root: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderBottom: "1px solid #e3e3e3",
        padding: "8px 20px",
    },
}))(MuiDialogTitle);

const DialogContent = withStyles(() => ({
    root: {
        paddingTop: "20px",
        paddingRight: "20px",
        paddingLeft: "20px",
        paddingBottom: "90px",
    },
}))(MuiDialogContent);



// Etiquetas en español de cada estado del ticket.
const STATUS_LABELS = {
    ABIERTO: "Abierto",
    EN_PROCESO: "En proceso",
    RESUELTO: "Resuelto",
    CERRADO: "Cerrado",
};

// Transiciones permitidas por el backend. Desde el estado actual solo se puede
// avanzar al estado destino indicado (flujo lineal, sin reabrir).
//   ABIERTO    -> EN_PROCESO (requiere assignedTo y priority)
//   EN_PROCESO -> RESUELTO   (requiere resolution)
//   RESUELTO   -> CERRADO
//   CERRADO    -> (terminal)
const NEXT_STATUS = {
    ABIERTO: "EN_PROCESO",
    EN_PROCESO: "RESUELTO",
    RESUELTO: "CERRADO",
    CERRADO: null,
};

// Normaliza el estado que viene del backend a uno de los valores del catálogo.
// Un ticket recién generado ("Nuevo"/"Abierto"/"Open") se mapea a "ABIERTO".
const normalizeStatusValue = (raw) => {
    const s = (raw ?? "").toString().trim().toLowerCase();
    if (["abierto", "nuevo", "new", "open", ""].includes(s)) return "ABIERTO";
    if (["en_proceso", "en proceso", "inprogress", "in progress", "en progreso"].includes(s)) return "EN_PROCESO";
    if (["resuelto", "resolved"].includes(s)) return "RESUELTO";
    if (["cerrado", "closed"].includes(s)) return "CERRADO";
    return raw || "ABIERTO";
};

// Formatea una fecha (ms, ISO o { $date }) a texto legible.
const formatDate = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "—";
    let value = raw;
    if (typeof raw === "object") value = raw.$date ?? raw.date ?? null;
    if (value === null || value === undefined) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

// Campo de detalle de solo lectura. NO se deshabilita: la información se
// muestra con texto normal (solo lectura, pero perfectamente legible).
const ReadOnlyField = ({ label, value }) => (
    <TextField
        label={label}
        value={value === "" || value === null || value === undefined ? "—" : value}
        fullWidth
        variant="outlined"
        size="small"
        InputProps={{ readOnly: true }}
        InputLabelProps={{ shrink: true }}
    />
);

/**
 * Modal de detalle de un ticket. Permite ver la información, asignar el ticket
 * a un agente y cambiar su estado.
 *
 * Props:
 *  - open: controla la visibilidad
 *  - ticket: ticket normalizado (con .raw para el detalle completo)
 *  - onBack(): cierra el modal
 *  - onSave({ assignedTo, status }): persiste; debe devolver true si tuvo éxito
 *
 * La asignación es automática: cuando el usuario cambia el estado de un ticket
 * que aún no tiene responsable, el ticket se asigna al usuario de la sesión.
 */
// Email del usuario logueado (según "recordar credenciales").
const getLoggedUserEmail = () => {
    const remeberAuthCredentials = localStorage.getItem("remeberAuthCredentials");
    return remeberAuthCredentials
        ? localStorage.getItem("email")
        : sessionStorage.getItem("email");
};

const TicketDetailModal = (props) => {
    const { open, ticket, priorityOptions = [], onBack, onSave } = props;

    // Estado actual del ticket (inmutable) y el destino al que se quiere mover.
    const [currentStatus, setCurrentStatus] = useState("ABIERTO");
    const [moveToNext, setMoveToNext] = useState(false); // avanzar al siguiente estado
    const [assignedTo, setAssignedTo] = useState("");
    const [priority, setPriority] = useState("");
    const [resolution, setResolution] = useState("");
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && ticket) {
            setCurrentStatus(normalizeStatusValue(ticket.status));
            setMoveToNext(false);
            setAssignedTo(ticket.assignedTo || "");
            setPriority(ticket.priority || "");
            setResolution("");
            setTags(Array.isArray(ticket.tags) ? ticket.tags : []);
            setTagInput("");
        }
    }, [open, ticket]);

    // Estado destino de la transición (si el usuario decide avanzar).
    const nextStatus = NEXT_STATUS[currentStatus];
    const targetStatus = moveToNext && nextStatus ? nextStatus : currentStatus;

    // El ticket cerrado es terminal: no se pueden hacer cambios.
    const isClosed = currentStatus === "CERRADO";
    // Las notas adicionales (tags) se pueden capturar:
    //  - mientras el ticket ya está EN_PROCESO (PATCH sin status), o
    //  - al asignarlo (transición ABIERTO -> EN_PROCESO).
    const canEditTags =
        !isClosed &&
        (currentStatus === "EN_PROCESO" || targetStatus === "EN_PROCESO");

    // ---- Etiquetas del proceso de atención (PATCH sin status) ----
    const addTag = () => {
        const value = (tagInput || "").trim();
        if (!value) return;
        const exists = tags.some((item) => item.toLowerCase() === value.toLowerCase());
        if (!exists) setTags([...tags, value]);
        setTagInput("");
    };

    const handleTagKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            addTag();
        }
    };

    // Al activar "avanzar al siguiente estado": si pasa a EN_PROCESO y el ticket
    // no tiene responsable, se asigna automáticamente al usuario logueado.
    const handleMoveToggle = (checked) => {
        setMoveToNext(checked);
        if (checked && nextStatus === "EN_PROCESO" && !assignedTo) {
            const loggedUser = getLoggedUserEmail();
            if (loggedUser) setAssignedTo(loggedUser);
        }
    };

    if (!ticket) return null;

    const raw = ticket.raw || {};
    const description = raw.description ?? raw.descripcion ?? raw.message ?? raw.mensaje ?? "";
    const email = raw.email ?? raw.correo ?? "";

    // Adjuntos: puede venir como arreglo (de URLs u objetos) o faltar.
    const rawAttachments = raw.attachments ?? raw.adjuntos ?? raw.files ?? [];
    const attachments = Array.isArray(rawAttachments)
        ? rawAttachments
        : rawAttachments
        ? [rawAttachments]
        : [];

    // Validación del botón guardar según la transición seleccionada.
    const isTransitioning = moveToNext && !!nextStatus;
    let disableSave = saving || isClosed;
    if (isTransitioning) {
        if (targetStatus === "EN_PROCESO") {
            // Requiere responsable y prioridad.
            disableSave = disableSave || !assignedTo || !priority;
        }
        if (targetStatus === "RESUELTO") {
            // Requiere la resolución (texto).
            disableSave = disableSave || !resolution.trim();
        }
    } else if (!canEditTags) {
        // Sin transición y sin poder editar tags: no hay nada que guardar.
        disableSave = true;
    }

    const handleSave = async () => {
        const ticketId = ticket.ticketId || ticket.folio;

        // Junta las notas adicionales (incluyendo la que quedó escrita sin agregar).
        const finalTags = [...tags];
        const pending = (tagInput || "").trim();
        if (pending && !finalTags.some((t) => t.toLowerCase() === pending.toLowerCase())) {
            finalTags.push(pending);
        }

        // Caso 1: transición de estado.
        if (isTransitioning) {
            let payload = { ticketId, status: targetStatus };
            if (targetStatus === "EN_PROCESO") {
                payload.assignedTo = assignedTo;
                payload.priority = priority;
                // Al asignar también se pueden capturar notas. El backend ignora
                // tags en la petición con status, así que se envían aparte: se
                // adjuntan como extraTags para que el contenedor haga el 2º PATCH.
                if (finalTags.length > 0) payload.extraTags = finalTags;
            } else if (targetStatus === "RESUELTO") {
                payload.resolution = resolution.trim();
            }
            // CERRADO solo lleva ticketId + status.

            setSaving(true);
            const ok = await onSave(payload);
            setSaving(false);
            if (ok) onBack();
            return;
        }

        // Caso 2: sin cambio de estado -> actualizar notas del proceso
        // (PATCH sin status), mientras el ticket está EN_PROCESO.
        setSaving(true);
        const ok = await onSave({ ticketId, tags: finalTags });
        setSaving(false);
        if (ok) onBack();
    };

    return (
        <Dialog open={!!open} fullScreen>
            <DialogTitle disableTypography>
                <h2 style={{ margin: 0, color: "#27315D" }}>
                    Ticket {ticket.folio}
                </h2>
                <IconButton onClick={onBack} disabled={saving}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <div style={{ maxWidth: 720, margin: "0 auto" }}>
                    {/* ---- Detalle del ticket (solo lectura) ---- */}
                    <div className="contacts-list-card">
                        <Typography variant="subtitle1" style={{ color: "#27315D", fontWeight: 600, marginBottom: 16 }}>
                            Detalle del ticket
                        </Typography>
                        <Grid container spacing={2}>
                            {/* Fila 1: Folio + Fecha de creación */}
                            <Grid item xs={12} sm={6}>
                                <ReadOnlyField label="Folio" value={ticket.folio} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ReadOnlyField label="Fecha de creación" value={formatDate(ticket.createdAt)} />
                            </Grid>
                            {/* Fila 2: Asunto */}
                            <Grid item xs={12}>
                                <ReadOnlyField label="Asunto" value={ticket.subject} />
                            </Grid>
                            {/* Fila 3: Descripción */}
                            <Grid item xs={12}>
                                <TextField
                                    label="Descripción"
                                    value={description || "—"}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    variant="outlined"
                                    size="small"
                                    InputProps={{ readOnly: true }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            {/* Nombre del cliente, justo después de la descripción */}
                            <Grid item xs={12} sm={6}>
                                <ReadOnlyField label="Nombre" value={ticket.contactName} />
                            </Grid>
                            {/* Contacto + Email */}
                            <Grid item xs={12} sm={6}>
                                <ReadOnlyField label="Contacto" value={ticket.contact} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ReadOnlyField label="Email" value={email} />
                            </Grid>
                            {/* Adjuntos: al final del detalle. Se muestra siempre,
                                aunque no haya ninguno. */}
                            <Grid item xs={12}>
                                <Typography
                                    variant="body2"
                                    style={{ color: "#27315D", fontWeight: 600, marginBottom: 6 }}
                                >
                                    Adjuntos
                                </Typography>
                                {attachments.length > 0 ? (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                                        {attachments.map((item, index) => {
                                            const href = typeof item === "string" ? item : item?.url;
                                            if (!href) return null;
                                            const name =
                                                typeof item === "string"
                                                    ? item.split("/").pop()
                                                    : item?.name || `Adjunto ${index + 1}`;
                                            // Se considera imagen por extensión o por el tipo del adjunto.
                                            const type = typeof item === "object" ? (item.type || item.mimeType || "") : "";
                                            const isImage =
                                                /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(href) ||
                                                /^image\//i.test(type);

                                            return isImage ? (
                                                // Imagen cargada en blob público: se pinta directa.
                                                // Al hacer clic se abre en grande en pestaña nueva.
                                                <a
                                                    key={index}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <img
                                                        src={href}
                                                        alt={name}
                                                        style={{
                                                            maxWidth: 160,
                                                            maxHeight: 160,
                                                            borderRadius: 8,
                                                            border: "1px solid #e7e8ee",
                                                            objectFit: "cover",
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </a>
                                            ) : (
                                                // No es imagen (ej. PDF): se deja como enlace.
                                                <a
                                                    key={index}
                                                    href={href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: "#27315D" }}
                                                >
                                                    {name}
                                                </a>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <Typography variant="body2" style={{ color: "#8a8a8a" }}>
                                        Sin adjuntos
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </div>

                    {/* ---- Administración: estado, transición y gestión ---- */}
                    <div className="contacts-list-card" style={{ marginTop: 16 }}>
                        <Typography variant="subtitle1" style={{ color: "#27315D", fontWeight: 600, marginBottom: 16 }}>
                            Administración
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            {/* Estado actual (solo lectura) y responsable */}
                            <Grid item xs={12} sm={6}>
                                <ReadOnlyField label="Estado actual" value={STATUS_LABELS[currentStatus] || currentStatus} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" style={{ color: "#6b6b6b" }}>
                                    Asignado a:{" "}
                                    <strong style={{ color: "#27315D" }}>
                                        {assignedTo || "—"}
                                    </strong>
                                </Typography>
                            </Grid>

                            {/* Opción para avanzar al siguiente estado (si no está cerrado) */}
                            {isClosed ? (
                                <Grid item xs={12}>
                                    <Typography variant="body2" style={{ color: "#8a8a8a" }}>
                                        El ticket está cerrado. No se pueden realizar más cambios.
                                    </Typography>
                                </Grid>
                            ) : (
                                nextStatus && (
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    color="primary"
                                                    checked={moveToNext}
                                                    onChange={(e) => handleMoveToggle(e.target.checked)}
                                                />
                                            }
                                            label={`Cambiar a "${STATUS_LABELS[nextStatus]}"`}
                                        />
                                    </Grid>
                                )
                            )}

                            {/* Campos requeridos para pasar a EN_PROCESO */}
                            {isTransitioning && targetStatus === "EN_PROCESO" && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <ReadOnlyField label="Responsable (tú)" value={assignedTo} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            label="Prioridad *"
                                            value={priority}
                                            onChange={(e) => setPriority(e.target.value)}
                                            fullWidth
                                            variant="outlined"
                                            size="small"
                                            InputLabelProps={{ shrink: true }}
                                        >
                                            <MenuItem value="">
                                                <em>Selecciona una prioridad</em>
                                            </MenuItem>
                                            {priorityOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </>
                            )}

                            {/* Campo requerido para pasar a RESUELTO */}
                            {isTransitioning && targetStatus === "RESUELTO" && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="Resolución *"
                                        value={resolution}
                                        onChange={(e) => setResolution(e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        variant="outlined"
                                        size="small"
                                        placeholder="Describe la solución final del ticket"
                                        helperText="Obligatorio para marcar el ticket como resuelto."
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            )}

                            {/* Notas adicionales (tags). Se muestran si el ticket ya
                                está EN_PROCESO, o al asignarlo (transición a EN_PROCESO).
                                El guardado decide si van en PATCH propio o tras asignar. */}
                            {canEditTags && (
                                <Grid item xs={12}>
                                    <Typography
                                        variant="body2"
                                        style={{ color: "#27315D", fontWeight: 600, marginBottom: 6 }}
                                    >
                                        Notas adicionales
                                    </Typography>
                                    <TextField
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={handleTagKeyDown}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        placeholder="Ej. requiere-info-cliente, escalado-nivel-2"
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Agregar etiqueta">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={addTag}
                                                                disabled={!tagInput.trim()}
                                                            >
                                                                <AddIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {/* Lista vertical del avance del proceso. No se pueden
                                        eliminar (sin tache): refleja el histórico de notas. */}
                                    {tags.length > 0 && (
                                        <ol style={{ margin: "10px 0 0", paddingLeft: 20, color: "#27315D" }}>
                                            {tags.map((item, index) => (
                                                <li key={`tag-${index}`} style={{ marginBottom: 4 }}>
                                                    {item}
                                                </li>
                                            ))}
                                        </ol>
                                    )}
                                </Grid>
                            )}
                        </Grid>
                    </div>
                </div>
            </DialogContent>

            <FooterControls
                onCancel={onBack}
                onSave={handleSave}
                disableSave={disableSave}
                saveButtonName="Guardar cambios"
                saveTitle="¿Estás seguro que deseas guardar los cambios del ticket?"
                saveMessage="Esta acción actualizará el ticket."
            />
        </Dialog>
    );
};

export default TicketDetailModal;
